import { useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useSettingsStore } from '../stores/useSettingsStore';
import useSubscriptionStore from '../stores/useSubscriptionStore';
import AnonymousBanner from './AnonymousBanner';
import OfflineBanner from './OfflineBanner';
import NudgeBanner from './NudgeBanner';

const NAV_ITEMS = [
  { to: '/', label: 'Record', icon: 'O', end: true },
  { to: '/logbook', label: 'Logbook', icon: '@' },
  { to: '/stats', label: 'Stats', icon: '#' },
  { to: '/friends', label: 'Friends', icon: '*' },
  { to: '/settings', label: 'Settings', icon: '=' },
];

const PAGE_TITLES = {
  '/': 'Record',
  '/logbook': 'Logbook',
  '/stats': 'Season Stats',
  '/friends': 'Friends',
  '/settings': 'Settings',
  '/premium': 'Premium',
  '/leaderboard': 'Leaderboard',
  '/map': 'Live Map',
  '/resorts': 'Resorts',
};

function ThemeToggle() {
  const nightMode = useSettingsStore((s) => s.nightMode);
  const toggleNightMode = useSettingsStore((s) => s.toggleNightMode);
  return (
    <button
      type="button"
      onClick={toggleNightMode}
      aria-label={nightMode ? 'Switch to light mode' : 'Switch to dark mode'}
      className="flex h-11 w-11 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100 dark:text-night-text dark:hover:bg-night-card"
    >
      <span aria-hidden="true" className="text-lg">
        {nightMode ? '☀️' : '🌙'}
      </span>
    </button>
  );
}

function PlanBadge() {
  // isPremium() (not raw tier) so an expired subscription drops the badge.
  const isPremium = useSubscriptionStore((s) => s.isPremium)();
  if (!isPremium) {
    return (
      <NavLink
        to="/premium"
        className="hidden rounded-full bg-sky-500 px-3 py-1 text-xs font-semibold text-white hover:bg-sky-600 sm:inline-flex"
      >
        Upgrade
      </NavLink>
    );
  }
  return (
    <span className="hidden rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 sm:inline-flex">
      Premium
    </span>
  );
}

function Brand() {
  return (
    <NavLink to="/" className="flex items-center gap-2">
      <span
        aria-hidden="true"
        className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-500 text-white"
      >
        <span className="text-sm font-bold">PL</span>
      </span>
      <span className="text-base font-bold text-gray-900 dark:text-night-text">PowderLog</span>
    </NavLink>
  );
}

function NavItem({ item, vertical = false }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        vertical
          ? `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              isActive
                ? 'bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400'
                : 'text-gray-700 hover:bg-gray-100 dark:text-night-text dark:hover:bg-night-card'
            }`
          : `flex h-full flex-1 flex-col items-center justify-center gap-0.5 text-xs font-medium transition-colors ${
              isActive
                ? 'text-sky-600 dark:text-sky-400'
                : 'text-gray-500 dark:text-night-muted'
            }`
      }
    >
      <span aria-hidden="true" className={vertical ? 'text-base' : 'text-lg'}>
        {item.icon}
      </span>
      <span>{item.label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const location = useLocation();
  const nightMode = useSettingsStore((s) => s.nightMode);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', !!nightMode);
  }, [nightMode]);
  // Note: previously a prefers-color-scheme listener overwrote nightMode on
  // every system change, silently wiping a user's explicit Light pick when
  // macOS rolled into Dark at sunset. The initial seed in useSettingsStore
  // already picks the system default on first load; thereafter the
  // persisted value wins.

  const title = PAGE_TITLES[location.pathname] || '';

  return (
    <div className="min-h-screen bg-gray-50 pb-20 dark:bg-night-bg md:pb-0 md:pl-64">
      <header className="safe-area-top sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur dark:border-night-card dark:bg-night-bg/95">
        <div className="mx-auto flex h-14 max-w-2xl items-center justify-between gap-3 px-4">
          <Brand />
          {title && (
            <h1 className="hidden truncate text-sm font-semibold text-gray-700 dark:text-night-text sm:block">
              {title}
            </h1>
          )}
          <div className="flex items-center gap-2">
            <PlanBadge />
            <ThemeToggle />
          </div>
        </div>
        <OfflineBanner />
        <AnonymousBanner />
      </header>

      <NudgeBanner />

      <main className="mx-auto max-w-2xl px-4 py-4">
        <Outlet />
      </main>

      <nav
        aria-label="Primary"
        className="safe-area-bottom fixed bottom-0 left-0 right-0 z-30 flex h-16 border-t border-gray-200 bg-white/95 backdrop-blur dark:border-night-card dark:bg-night-bg/95 md:hidden"
      >
        {NAV_ITEMS.map((item) => (
          <NavItem key={item.to} item={item} />
        ))}
      </nav>

      <aside className="hidden fixed bottom-0 left-0 top-0 w-64 border-r border-gray-200 bg-white p-4 dark:border-night-card dark:bg-night-surface md:flex md:flex-col">
        <div className="mb-6">
          <Brand />
        </div>
        <nav aria-label="Sidebar" className="flex flex-col gap-1">
          {NAV_ITEMS.map((item) => (
            <NavItem key={item.to} item={item} vertical />
          ))}
        </nav>
      </aside>
    </div>
  );
}
